const THREE_MODULE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.1/build/three.module.js';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const easeOutSine = (t) => Math.sin(t * (Math.PI / 2));
const easeOutQuad = (t) => t * (2 - t);

let threePromise;

class RippleTexture {
  constructor(THREE, size = 128) {
    this.THREE = THREE;
    this.size = size;
    this.radius = size * 0.07;
    this.maxAge = 66;
    this.points = [];
    this.last = null;

    this.canvas = document.createElement('canvas');
    this.canvas.width = size;
    this.canvas.height = size;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: false });
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;
    this.clear();
  }

  clear() {
    this.ctx.shadowColor = 'transparent';
    this.ctx.shadowBlur = 0;
    this.ctx.fillStyle = 'rgb(0, 0, 0)';
    this.ctx.fillRect(0, 0, this.size, this.size);
  }

  addPoint(point) {
    let force = 0.28;
    let vx = 0;
    let vy = 0;

    if (this.last) {
      const relativeX = point.x - this.last.x;
      const relativeY = point.y - this.last.y;
      const distanceSquared = relativeX * relativeX + relativeY * relativeY;
      const distance = Math.sqrt(distanceSquared);

      if (distance > 0.002) {
        vx = relativeX / distance;
        vy = relativeY / distance;
        force = Math.min(distanceSquared * 7400, 0.82);
      }
    }

    this.last = { x: point.x, y: point.y };
    this.points.push({ x: point.x, y: point.y, age: 0, force, vx, vy });

    if (this.points.length > 78) {
      this.points.shift();
    }
  }

  reset() {
    this.last = null;
  }

  update() {
    this.clear();

    const agePart = 1 / this.maxAge;
    this.points = this.points.filter((point) => {
      const slowAsOlder = 1 - point.age / this.maxAge;
      const force = point.force * agePart * slowAsOlder;

      point.x += point.vx * force;
      point.y += point.vy * force;
      point.age += 1;

      return point.age <= this.maxAge;
    });

    this.points.forEach((point) => this.drawPoint(point));
    this.texture.needsUpdate = true;

    return this.points.length > 0;
  }

  drawPoint(point) {
    const position = {
      x: point.x * this.size,
      y: point.y * this.size,
    };

    let intensity;
    if (point.age < this.maxAge * 0.28) {
      intensity = easeOutSine(point.age / (this.maxAge * 0.28));
    } else {
      intensity = easeOutQuad(1 - (point.age - this.maxAge * 0.28) / (this.maxAge * 0.72));
    }
    intensity *= point.force;

    const red = ((point.vx + 1) / 2) * 255;
    const green = ((point.vy + 1) / 2) * 255;
    const blue = intensity * 255;
    const offset = this.size * 5;

    this.ctx.save();
    this.ctx.shadowOffsetX = offset;
    this.ctx.shadowOffsetY = offset;
    this.ctx.shadowBlur = this.radius;
    this.ctx.shadowColor = `rgba(${red}, ${green}, ${blue}, ${0.34 * intensity})`;
    this.ctx.fillStyle = 'rgba(255, 0, 0, 1)';
    this.ctx.beginPath();
    this.ctx.arc(position.x - offset, position.y - offset, this.radius, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();
  }
}

class LiquidImageDistortion {
  constructor(THREE, stage) {
    this.THREE = THREE;
    this.stage = stage;
    this.image = stage.querySelector('img');
    this.isBackgroundStage = !this.image;
    this.usesPersistentCanvas = stage.matches(
      '.foria-page__media, .foria-home__house-photo, .foria-page__contact-hero, .foria-template-hero--visitas'
    );
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.animationFrame = null;
    this.isActive = false;
    this.isReady = false;
    this.sourceImage = null;
    this.resizeObserver = null;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'foria-liquid-image-canvas';
    this.stage.append(this.canvas);

    this.sourceCanvas = document.createElement('canvas');
    this.sourceContext = this.sourceCanvas.getContext('2d');
    this.rippleTexture = new RippleTexture(THREE);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
      premultipliedAlpha: false,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(this.pixelRatio);

    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    this.camera.position.z = 1;

    this.sourceTexture = new THREE.CanvasTexture(this.sourceCanvas);
    this.sourceTexture.minFilter = THREE.LinearFilter;
    this.sourceTexture.magFilter = THREE.LinearFilter;
    this.sourceTexture.generateMipmaps = false;

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      uniforms: {
        uImage: { value: this.sourceTexture },
        uRipple: { value: this.rippleTexture.texture },
        uStrength: { value: 0.038 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision mediump float;
        uniform sampler2D uImage;
        uniform sampler2D uRipple;
        uniform float uStrength;
        varying vec2 vUv;

        void main() {
          vec4 ripple = texture2D(uRipple, vUv);
          float vx = -(ripple.r * 2.0 - 1.0);
          float vy = -(ripple.g * 2.0 - 1.0);
          float intensity = ripple.b;
          vec2 uv = vUv + vec2(vx, vy) * intensity * uStrength;
          vec4 color = texture2D(uImage, uv);
          gl_FragColor = color;
        }
      `,
    });

    this.scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material));

    this.resize = this.resize.bind(this);
    this.render = this.render.bind(this);
    this.refreshSource = this.refreshSource.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);

    this.prepare();
  }

  async prepare() {
    try {
      this.sourceImage = this.image || (await this.loadBackgroundImage());

      if (!this.sourceImage) {
        this.destroy();
        return;
      }

      await this.waitForImage(this.sourceImage);
      if (this.isBackgroundStage) {
        this.stage.classList.add('foria-liquid-image--background');
      }
      this.attach();
      this.resize();
      this.renderer.render(this.scene, this.camera);
      this.stage.classList.add('foria-liquid-image--ready');
      if (this.usesPersistentCanvas) {
        this.stage.classList.add('foria-liquid-image--persistent');
      }
      this.isReady = true;
    } catch (error) {
      this.destroy();
      console.warn('Foria image liquid hover could not initialize.', error);
    }
  }

  attach() {
    this.stage.addEventListener('pointerenter', this.handlePointerEnter);
    this.stage.addEventListener('pointermove', this.handlePointerMove);
    this.stage.addEventListener('pointerleave', this.handlePointerLeave);
    this.stage.addEventListener('pointercancel', this.handlePointerLeave);
    window.addEventListener('resize', this.resize);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this.stage);
    }
  }

  destroy() {
    this.stage.classList.remove(
      'foria-liquid-image--ready',
      'foria-liquid-image--active',
      'foria-liquid-image--background',
      'foria-liquid-image--persistent'
    );

    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('resize', this.resize);
    this.stage.removeEventListener('pointerenter', this.handlePointerEnter);
    this.stage.removeEventListener('pointermove', this.handlePointerMove);
    this.stage.removeEventListener('pointerleave', this.handlePointerLeave);
    this.stage.removeEventListener('pointercancel', this.handlePointerLeave);

    if (this.canvas.parentNode === this.stage) {
      this.canvas.remove();
    }

    this.material?.dispose();
    this.sourceTexture?.dispose();
    this.rippleTexture?.texture.dispose();
    this.renderer?.dispose();
  }

  handlePointerEnter(event) {
    if (!this.isReady) return;

    this.refreshSource();
    this.isActive = true;
    this.stage.classList.add('foria-liquid-image--active');
    this.addPointer(event);
    this.start();
  }

  handlePointerMove(event) {
    if (!this.isReady) return;

    if (!this.isActive) {
      this.refreshSource();
      this.isActive = true;
      this.stage.classList.add('foria-liquid-image--active');
    }

    this.addPointer(event);
    this.start();
  }

  handlePointerLeave() {
    this.isActive = false;
    this.stage.classList.remove('foria-liquid-image--active');
    this.rippleTexture.reset();
  }

  addPointer(event) {
    const rect = this.stage.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    if (x < 0 || x > 1 || y < 0 || y > 1) return;

    this.rippleTexture.addPoint({ x, y });
  }

  start() {
    if (!this.animationFrame) {
      this.animationFrame = window.requestAnimationFrame(this.render);
    }
  }

  resize() {
    if (!this.sourceImage) return;

    const rect = this.stage.getBoundingClientRect();
    const width = Math.max(1, Math.ceil(rect.width));
    const height = Math.max(1, Math.ceil(rect.height));
    const canvasWidth = Math.ceil(width * this.pixelRatio);
    const canvasHeight = Math.ceil(height * this.pixelRatio);

    this.renderer.setSize(width, height, false);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.sourceCanvas.width = canvasWidth;
    this.sourceCanvas.height = canvasHeight;
    this.drawSource(canvasWidth, canvasHeight);
    this.sourceTexture.needsUpdate = true;
  }

  refreshSource() {
    if (!this.sourceImage) return;

    const rect = this.stage.getBoundingClientRect();
    const width = Math.max(1, Math.ceil(rect.width));
    const height = Math.max(1, Math.ceil(rect.height));
    const canvasWidth = Math.ceil(width * this.pixelRatio);
    const canvasHeight = Math.ceil(height * this.pixelRatio);

    if (this.sourceCanvas.width !== canvasWidth || this.sourceCanvas.height !== canvasHeight) {
      this.resize();
      return;
    }

    this.drawSource(canvasWidth, canvasHeight);
    this.sourceTexture.needsUpdate = true;
    this.renderer.render(this.scene, this.camera);
  }

  render() {
    const hasRipples = this.rippleTexture.update();
    this.renderer.render(this.scene, this.camera);

    if (this.isActive || hasRipples) {
      this.animationFrame = window.requestAnimationFrame(this.render);
    } else {
      this.animationFrame = null;
    }
  }

  drawSource(width, height) {
    const ctx = this.sourceContext;
    ctx.clearRect(0, 0, width, height);

    if (this.image) {
      this.drawElementImage(ctx, width, height);
      return;
    }

    this.drawBackgroundImage(ctx, width, height);
  }

  drawElementImage(ctx, width, height) {
    const style = window.getComputedStyle(this.image);
    const fit = style.objectFit || 'fill';
    const position = parsePosition(style.objectPosition || '50% 50%');
    const imageWidth = this.image.naturalWidth || width;
    const imageHeight = this.image.naturalHeight || height;
    const stageRect = this.stage.getBoundingClientRect();
    const imageRect = this.image.getBoundingClientRect();

    if (!stageRect.width || !stageRect.height || !imageRect.width || !imageRect.height) {
      return;
    }

    const insetLeft = cssPixels(style.borderLeftWidth) + cssPixels(style.paddingLeft);
    const insetRight = cssPixels(style.borderRightWidth) + cssPixels(style.paddingRight);
    const insetTop = cssPixels(style.borderTopWidth) + cssPixels(style.paddingTop);
    const insetBottom = cssPixels(style.borderBottomWidth) + cssPixels(style.paddingBottom);
    const contentWidth = Math.max(1, imageRect.width - insetLeft - insetRight);
    const contentHeight = Math.max(1, imageRect.height - insetTop - insetBottom);
    const offsetX = (imageRect.left - stageRect.left + insetLeft) * this.pixelRatio;
    const offsetY = (imageRect.top - stageRect.top + insetTop) * this.pixelRatio;
    const drawWidth = contentWidth * this.pixelRatio;
    const drawHeight = contentHeight * this.pixelRatio;

    drawFittedImage(ctx, this.image, drawWidth, drawHeight, imageWidth, imageHeight, fit, position, offsetX, offsetY);
  }

  drawBackgroundImage(ctx, width, height) {
    const style = window.getComputedStyle(this.stage);
    const position = parsePosition((style.backgroundPosition || '50% 50%').split(',')[0]);
    const imageWidth = this.sourceImage.naturalWidth || width;
    const imageHeight = this.sourceImage.naturalHeight || height;

    drawFittedImage(ctx, this.sourceImage, width, height, imageWidth, imageHeight, 'cover', position);
  }

  async loadBackgroundImage() {
    const backgroundImage = window.getComputedStyle(this.stage).backgroundImage;
    const imageUrl = extractFirstImageUrl(backgroundImage);

    if (!imageUrl) return null;

    const image = new Image();
    image.decoding = 'async';
    image.src = imageUrl;

    return image;
  }

  waitForImage(image) {
    if (image.complete && image.naturalWidth > 0) {
      return Promise.resolve();
    }

    if (image.decode) {
      return image.decode().catch(
        () =>
          new Promise((resolve, reject) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', reject, { once: true });
          })
      );
    }

    return new Promise((resolve, reject) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', reject, { once: true });
    });
  }
}

function drawFittedImage(ctx, image, width, height, imageWidth, imageHeight, fit, position, offsetX = 0, offsetY = 0) {
  if (fit === 'fill') {
    ctx.drawImage(image, offsetX, offsetY, width, height);
    return;
  }

  const scale =
    fit === 'cover'
      ? Math.max(width / imageWidth, height / imageHeight)
      : Math.min(width / imageWidth, height / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  const x = (width - drawWidth) * position.x;
  const y = (height - drawHeight) * position.y;

  ctx.drawImage(image, offsetX + x, offsetY + y, drawWidth, drawHeight);
}

function extractFirstImageUrl(backgroundImage) {
  const match = backgroundImage.match(/url\((["']?)(.*?)\1\)/);
  return match?.[2] || null;
}

function cssPixels(value) {
  const pixels = Number.parseFloat(value);
  return Number.isFinite(pixels) ? pixels : 0;
}

function parsePosition(value) {
  const normalized = value.trim().toLowerCase();
  const parts = normalized.split(/\s+/);
  const xPart = parts[0] || '50%';
  const yPart = parts[1] || (isVerticalKeyword(xPart) ? xPart : '50%');

  return {
    x: parsePositionPart(isVerticalKeyword(xPart) ? '50%' : xPart, 'x'),
    y: parsePositionPart(yPart, 'y'),
  };
}

function parsePositionPart(part, axis) {
  if (part === 'left' || part === 'top') return 0;
  if (part === 'right' || part === 'bottom') return 1;
  if (part === 'center') return 0.5;

  const percent = Number.parseFloat(part);
  if (Number.isFinite(percent) && part.includes('%')) return percent / 100;

  return axis === 'y' && part === 'bottom' ? 1 : 0.5;
}

function isVerticalKeyword(value) {
  return value === 'top' || value === 'bottom';
}

async function getThree() {
  if (!threePromise) {
    threePromise = import(THREE_MODULE_URL);
  }

  return threePromise;
}

async function initLiquidImages(root = document) {
  if (prefersReducedMotion.matches) return;

  const stages = [...root.querySelectorAll('[data-foria-liquid-image]')].filter(
    (stage) => stage.dataset.foriaLiquidImageReady !== 'true'
  );

  if (!stages.length) return;

  let THREE;
  try {
    THREE = await getThree();
  } catch (error) {
    console.warn('Foria image liquid hover could not load Three.js.', error);
    return;
  }

  stages.forEach((stage) => {
    stage.dataset.foriaLiquidImageReady = 'true';
    new LiquidImageDistortion(THREE, stage);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initLiquidImages(), { once: true });
} else {
  initLiquidImages();
}

document.addEventListener('shopify:section:load', (event) => initLiquidImages(event.target));
